import Extension from "../base/extension";

/**
 * Menus
 * =============================================================================
 * Click-toggled dropdown menus: the account menu, the locale and language
 * pickers, the inline facet menus, the quick-order sort menu. Replaces
 * Bootstrap's Dropdown component, and with it @popperjs/core.
 *
 * WHY NOT [popover] + ANCHOR POSITIONING
 * That was the obvious plan, and it is wrong for these menus. A popover is in
 * the TOP LAYER, so nothing but anchor positioning can place it under its
 * trigger; anchor positioning is unsupported before Safari 26 and Firefox 151,
 * and the `popover` attribute is static markup you cannot progressively enhance
 * out of. Those browsers would get a menu pinned to the middle of the viewport.
 * Bootstrap's plain `.dropdown-menu` CSS (absolute, `top: 100%`) already places
 * these correctly in every browser with no measuring at all, which is exactly
 * what the hover megamenus have always relied on. Popper was only ever earning
 * its keep for the localization dropup, which needs to escape a clipping
 * ancestor; that one keeps its own treatment.
 *
 * MARKUP CONTRACT
 *   <div class="dropdown">                       <- or any positioned wrapper
 *     <button data-menu-toggle aria-expanded="false">…</button>
 *     <ul class="dropdown-menu">…</ul>           <- Bootstrap's positioning CSS
 *   </div>
 * The open state is the `menu-open` class on the wrapper, which the CSS turns
 * into `display: block` on the menu. `aria-expanded` is mirrored on the trigger.
 *
 * All listeners are delegated from document, so markup injected later (facet
 * re-renders, cart re-renders, quick view) needs no re-initialisation.
 */
class Menus extends Extension {
  name = "Menus";

  #openWrapper = null;
  #reposition = null;

  init() {
    document.addEventListener("click", this.#onClick.bind(this));
    document.addEventListener("keydown", this.#onKeydown.bind(this));

    // A menu left open across a section re-render would keep a wrapper
    // reference that is no longer in the document, so the next outside-click
    // would try to close a detached node. Cheaper to verify on use than to
    // observe the DOM: see #close().
    document.addEventListener("focusin", (event) => {
      if (!this.#openWrapper) return;
      if (!this.#openWrapper.contains(event.target)) this.#close(false);
    });
  }

  #onClick(event) {
    const trigger = event.target.closest("[data-menu-toggle]");

    if (trigger) {
      // Triggers are real buttons, but they sit inside forms often enough
      // (locale pickers) that a stray submit is worth ruling out.
      event.preventDefault();
      const wrapper = this.#wrapperFor(trigger);
      if (!wrapper) return;
      if (wrapper === this.#openWrapper) this.#close(true);
      else this.#open(wrapper);
      return;
    }

    // A click inside the open menu is a selection, not a dismissal: let links
    // and form controls do their job, then close.
    if (this.#openWrapper?.contains(event.target)) {
      if (event.target.closest("a, button, label")) this.#close(false);
      return;
    }

    this.#close(false);
  }

  #onKeydown(event) {
    if (event.key === "Escape" || event.key === "Esc") {
      if (this.#openWrapper) {
        event.stopPropagation();
        this.#close(true);
      }
      return;
    }

    const trigger = event.target.closest("[data-menu-toggle]");
    if (trigger && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      event.preventDefault();
      const wrapper = this.#wrapperFor(trigger);
      if (!wrapper) return;
      if (wrapper !== this.#openWrapper) this.#open(wrapper);
      const items = this.#itemsIn(wrapper);
      const target = event.key === "ArrowDown" ? items[0] : items[items.length - 1];
      target?.focus();
      return;
    }

    if (!this.#openWrapper?.contains(event.target)) return;

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const items = this.#itemsIn(this.#openWrapper);
      const index = items.indexOf(document.activeElement);
      const step = event.key === "ArrowDown" ? 1 : -1;
      // Wrap around: from the last item, down returns to the first.
      const next = items[(index + step + items.length) % items.length];
      next?.focus();
    }
  }

  #wrapperFor(trigger) {
    // An explicit target wins, for the cases where the menu cannot be a sibling
    // of its trigger. Otherwise the nearest positioned wrapper.
    const selector = trigger.dataset.menuToggle;
    if (selector) return document.querySelector(selector);
    return trigger.closest(".dropdown, .dropup, [data-menu]");
  }

  #itemsIn(wrapper) {
    const menu = wrapper.querySelector(".dropdown-menu");
    if (!menu) return [];
    return Array.from(
      menu.querySelectorAll("a[href], button:not([disabled]), input:not([disabled]), select:not([disabled])")
    ).filter((element) => element.offsetParent !== null);
  }

  /**
   * Place a top-layer menu against its trigger.
   *
   * Only the localization picker needs this. It sits inside a footer section
   * carrying `content-visibility: auto`, whose paint containment clipped the
   * menu; that is what Popper's `strategy: "fixed"` was working around, and what
   * the top layer solves outright, since content-visibility cannot suppress it.
   *
   * Measured in JS rather than with CSS anchor positioning on purpose: anchor
   * positioning is unsupported before Safari 26 and Firefox 151, and a top-layer
   * element has no other way to find its trigger, so those browsers would get a
   * menu in the middle of the viewport. Twelve lines here beats shipping Popper
   * for one control, and beats a support cliff.
   */
  #positionPopover(menu, trigger) {
    const anchor = trigger.getBoundingClientRect();
    const GAP = 4;

    // The menu's own cap, read from CSS so the snippet keeps owning it. Measured
    // before any max-height we set below, or repeat opens would ratchet it down.
    menu.style.maxHeight = "";
    const menuBox = menu.getBoundingClientRect();
    const cap = menuBox.height;

    // Prefer opening upward (it is a dropup) and SHRINK to fit rather than
    // flipping below. Flipping looked detached on a phone, where the footer sits
    // high enough that 300px of menu rarely fits above it: the menu jumped to the
    // far side of the trigger and covered unrelated footer content. Staying put
    // and scrolling internally keeps it visibly attached to what opened it.
    const spaceAbove = anchor.top - GAP;
    const spaceBelow = window.innerHeight - anchor.bottom - GAP;
    // Biased upward, because this is a dropup in a footer: opening downward there
    // usually means covering the page edge or running off it. Only flip when
    // there is genuinely not a useful amount of room above.
    const MIN_USEFUL_HEIGHT = 160;
    const openUp = spaceAbove >= Math.min(cap, MIN_USEFUL_HEIGHT) || spaceAbove >= spaceBelow;
    const height = Math.min(cap, openUp ? spaceAbove : spaceBelow);
    menu.style.maxHeight = `${Math.max(0, Math.round(height))}px`;
    const top = openUp ? anchor.top - height - GAP : anchor.bottom + GAP;

    // End-aligned, clamped so a long country list cannot overflow either edge.
    let left = anchor.right - menuBox.width;
    left = Math.max(GAP, Math.min(left, window.innerWidth - menuBox.width - GAP));

    menu.style.position = "fixed";
    menu.style.margin = "0";
    menu.style.inset = "auto";
    menu.style.top = `${Math.round(top)}px`;
    menu.style.left = `${Math.round(left)}px`;
  }

  /**
   * Keep a plain (non-popover) menu inside the viewport: flip it above its
   * trigger when there is more room there, and cap its height to whatever space
   * it ends up with so the overflow can be scrolled.
   *
   * Both halves are needed and the first alone is a trap. An absolutely
   * positioned menu contributes NO height to the document, so a long menu at the
   * bottom of the page runs off it with nothing left to scroll to. Flipping it
   * up without a cap just moves the same overflow off the TOP, where it is
   * equally unreachable: the markets bar showed two countries out of sixteen
   * with no way to reach the rest. The `[popover]` menus already had a cap in
   * CSS, which is why the footer picker behaved and this one did not.
   *
   * Placement stays in CSS (a class); only the height is measured, and only when
   * the menu does not already fit.
   */
  #fitToViewport(wrapper, menu, trigger) {
    wrapper.classList.remove("menu-flip-up");
    menu.style.maxHeight = "";
    menu.style.overflowY = "";

    const GAP = 4;
    const anchor = trigger.getBoundingClientRect();
    const natural = menu.getBoundingClientRect().height;
    const spaceBelow = window.innerHeight - anchor.bottom - GAP;
    const spaceAbove = anchor.top - GAP;

    if (natural <= spaceBelow) return;

    const openUp = spaceAbove > spaceBelow;
    if (openUp) wrapper.classList.add("menu-flip-up");

    // Floor it: a menu squeezed to a sliver is worse than one that overhangs
    // slightly, and on a very short viewport neither side has real room.
    const MIN_HEIGHT = 120;
    const room = Math.max(openUp ? spaceAbove : spaceBelow, MIN_HEIGHT);
    menu.style.maxHeight = `${Math.round(Math.min(natural, room))}px`;
    menu.style.overflowY = "auto";
  }

  #open(wrapper) {
    // One at a time: opening a second menu closes the first, which is what
    // Bootstrap did and what shoppers expect from a menu bar.
    if (this.#openWrapper && this.#openWrapper !== wrapper) this.#close(false);
    wrapper.classList.add("menu-open");
    const trigger = wrapper.querySelector("[data-menu-toggle]");
    trigger?.setAttribute("aria-expanded", "true");
    this.#openWrapper = wrapper;

    const plainMenu = wrapper.querySelector(".dropdown-menu:not([popover])");
    if (plainMenu && trigger) this.#fitToViewport(wrapper, plainMenu, trigger);

    const menu = wrapper.querySelector(".dropdown-menu[popover]");
    if (menu && trigger) {
      // `popover="manual"` throughout, so dismissal stays in one place (this
      // extension) rather than being split between us and the browser's own
      // light dismiss, which would fight over the same clicks.
      menu.showPopover();
      this.#positionPopover(menu, trigger);
      // Fixed to the viewport, so it has to follow the trigger when the page
      // moves underneath it. Passive: this only reads geometry.
      this.#reposition = () => this.#positionPopover(menu, trigger);
      window.addEventListener("scroll", this.#reposition, { passive: true });
      window.addEventListener("resize", this.#reposition, { passive: true });
      // iOS moves the VISUAL viewport independently of the layout viewport as
      // the toolbars collapse and during momentum scrolling, and window scroll
      // events lag behind it. Without these the menu drifts away from its
      // trigger on a phone while looking correct on a desktop at the same width.
      window.visualViewport?.addEventListener("scroll", this.#reposition, { passive: true });
      window.visualViewport?.addEventListener("resize", this.#reposition, { passive: true });
    }
  }

  #close(returnFocus) {
    const wrapper = this.#openWrapper;
    this.#openWrapper = null;
    if (!wrapper) return;

    wrapper.classList.remove("menu-open");
    wrapper.classList.remove("menu-flip-up");
    // Clear what #fitToViewport measured, so the next open starts from the
    // menu's natural height rather than ratcheting down from the last one.
    const plain = wrapper.querySelector(".dropdown-menu:not([popover])");
    if (plain) {
      plain.style.maxHeight = "";
      plain.style.overflowY = "";
    }
    const trigger = wrapper.querySelector("[data-menu-toggle]");
    trigger?.setAttribute("aria-expanded", "false");

    const menu = wrapper.querySelector(".dropdown-menu[popover]");
    if (menu?.matches(":popover-open")) menu.hidePopover();
    if (menu) menu.style.maxHeight = "";
    if (this.#reposition) {
      window.removeEventListener("scroll", this.#reposition);
      window.removeEventListener("resize", this.#reposition);
      window.visualViewport?.removeEventListener("scroll", this.#reposition);
      window.visualViewport?.removeEventListener("resize", this.#reposition);
      this.#reposition = null;
    }
    // Only pull focus back for a deliberate dismissal (Escape, or clicking the
    // trigger again). Doing it on an outside click would yank focus away from
    // whatever the shopper actually clicked.
    if (returnFocus && trigger?.isConnected) trigger.focus();
  }
}

export default Menus;
